/**
 * 활동 로그 페이지 컴포넌트
 *
 * 리팩토링: 552줄 → ~200줄
 * - ActivityLogFilters: 필터 UI 컴포넌트
 * - ActivityLogTable: 테이블 렌더링 컴포넌트
 * - CustomerAutocomplete: 공통 UI 컴포넌트 (재사용 가능)
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Pagination } from '@/components/ui/Pagination';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAuth } from '@/contexts/AuthContext';
import { userActivityTypes, superAdminUserLogTypes, superAdminSystemLogTypes } from '@/constants/activityLog';
import { useActivityLogs, ActivityLogFilters as FilterType } from '@/hooks/common';
import { adminAPI } from '@/services/api';
import { Customer } from '@/types';
import { PAGINATION } from '@/constants';
import { ActivityLogFilters, ActivityLogTable } from './components';

interface ActivityLogsProps {
  customerId?: number | null;
  logType?: 'user_logs' | 'system_logs';
}

const ActivityLogs: React.FC<ActivityLogsProps> = ({ customerId, logType = 'user_logs' }) => {
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(PAGINATION.DEFAULT_PAGE_SIZE);
  const [customers, setCustomers] = useState<Customer[]>([]);

  const isSaasAdmin = user?.role === 'saas_admin';
  const isAllLogsView = isSaasAdmin && customerId === null;

  // 활동 유형 목록 선택
  const getActivityTypes = () => {
    if (isSaasAdmin) {
      const isOwnLog = customerId !== undefined && customerId !== null && customerId === user?.customer?.id;
      if (isOwnLog) return userActivityTypes;
      if (logType === 'system_logs') return superAdminSystemLogTypes;
      return superAdminUserLogTypes;
    }
    return userActivityTypes;
  };

  // logType에 따라 activityCategory 자동 설정
  const getInitialActivityCategory = () => {
    if (isSaasAdmin && customerId === null) {
      return logType === 'system_logs' ? 'admin_activity,system' : 'user_activity';
    }
    return 'user_activity';
  };

  const initialCustomerId = customerId !== undefined
    ? (customerId !== null ? customerId : undefined)
    : (isSaasAdmin && user?.customer?.id ? user.customer.id : undefined);

  const [filters, setFilters] = useState<FilterType>({
    startDate: '',
    endDate: '',
    customerId: initialCustomerId,
    userSearch: '',
    activityType: 'all',
    activityCategory: getInitialActivityCategory(),
    descriptionSearch: '',
    logLevel: 'all',
  });

  // customerId 또는 logType이 변경되면 필터 업데이트
  useEffect(() => {
    if (customerId !== undefined) {
      setFilters(prev => ({
        ...prev,
        customerId: customerId !== null ? customerId : undefined,
        activityCategory: getInitialActivityCategory(),
      }));
    }
  }, [customerId, logType]);

  // 고객사 목록 로드 (슈퍼관리자만)
  useEffect(() => {
    if (isSaasAdmin) {
      const fetchCustomers = async () => {
        try {
          const response = await adminAPI.getCustomers(0, 1000, false);
          setCustomers(response.customers || []);
        } catch (error) {
          console.error('Failed to fetch customers:', error);
        }
      };
      fetchCustomers();
    }
  }, [isSaasAdmin]);

  const {
    logs,
    loading,
    error,
    setError,
    total,
    totalPages,
    fetchLogs,
  } = useActivityLogs(filters, currentPage, itemsPerPage);

  const handleResetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      customerId: initialCustomerId,
      userSearch: '',
      activityType: 'all',
      activityCategory: getInitialActivityCategory(),
      descriptionSearch: '',
      logLevel: 'all',
    });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // 페이지 타이틀 및 설명
  const getPageTitle = () => {
    if (isAllLogsView) {
      return logType === 'system_logs' ? '시스템 로그' : '전체 이용자 로그';
    }
    return '이용자 로그';
  };

  const getPageDescription = () => {
    if (isAllLogsView) {
      if (logType === 'system_logs') {
        return '시스템 및 관리자 활동 로그를 조회할 수 있습니다. (서비스 관리, 고객사 관리, nginx 오류 등)';
      }
      return '전체 이용자의 활동 로그를 조회할 수 있습니다. (로그인, 결제, 서비스 이용 등)';
    }
    if (isSaasAdmin && customerId !== null) {
      return '본인의 이용자 로그를 조회할 수 있습니다.';
    }
    if (user?.role === 'admin') {
      return '본인 고객사의 이용자 로그를 조회할 수 있습니다.';
    }
    return '본인의 활동 로그를 조회할 수 있습니다.';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{getPageTitle()}</CardTitle>
        <CardDescription>{getPageDescription()}</CardDescription>
      </CardHeader>
      <CardContent>
        <ActivityLogFilters
          filters={filters}
          onFilterChange={setFilters}
          onSearch={fetchLogs}
          onReset={handleResetFilters}
          loading={loading}
          activityTypes={getActivityTypes()}
          showCustomerFilter={isAllLogsView}
          showCategoryFilter={false}
          showLogLevelFilter={isAllLogsView}
          customers={customers}
        />

        {error && (
          <AlertMessage type="error" message={error} onClose={() => setError('')} />
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" message="로딩 중..." />
          </div>
        ) : (
          <>
            <ActivityLogTable
              logs={logs}
              showCustomerColumn={isSaasAdmin}
              isAllLogsView={isAllLogsView}
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={(value) => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
              showItemsPerPage={true}
              showTotal={true}
              className="mt-4"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityLogs;
